import { motion } from 'framer-motion';

/**
 * SabrilPixarAvatar — Reusable Pixar-style avatar of Sabril used across the audit
 * to humanize the diagnostic experience.
 *
 * Variants:
 *   - bubble: floating message bubble with avatar + speech (use for narrative beats)
 *   - inline: simple circle avatar (for inline mentions)
 *   - hero: big avatar with halo for top-of-section anchors
 */

const PIXAR_AVATAR = 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/xf03ot6r_sabril.png';

export const SABRIL_PIXAR_AVATAR_URL = PIXAR_AVATAR;

export const SabrilPixarAvatar = ({ size = 48, glow = true, className = '', objectPosition = '50% 18%' }) => (
  <motion.div
    className={`relative flex-shrink-0 ${className}`}
    style={{ width: size, height: size }}
    animate={{ y: [0, -1.5, 0] }}
    transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
  >
    {glow && (
      <motion.div
        className="absolute -inset-1 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(240,90,26,0.45) 0%, transparent 70%)' }}
        animate={{ opacity: [0.55, 0.85, 0.55], scale: [1, 1.07, 1] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
      />
    )}
    <div
      className="relative w-full h-full rounded-full overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #FBBF24 0%, #F05A1A 100%)',
        boxShadow: `0 6px 18px rgba(240,90,26,0.40), 0 0 0 ${Math.max(2, size / 24)}px rgba(255,255,255,0.18) inset`,
      }}
    >
      <img
        src={PIXAR_AVATAR}
        alt="Sabril"
        className="w-full h-full object-cover"
        style={{ objectPosition }}
        draggable={false}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    </div>
    <span
      className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2"
      style={{ background: '#22C55E', borderColor: '#0A0A0A' }}
    />
  </motion.div>
);

/**
 * SabrilSpeechBubble — Avatar + message bubble layout. Used to insert
 * Sabril "narration" moments inside the audit (humanizes the cold data).
 */
export const SabrilSpeechBubble = ({ message, kicker = 'Sabril', tone = 'orange', size = 48, testid = 'sabril-speech-bubble' }) => {
  const tones = {
    orange: { bg: 'rgba(240,90,26,0.12)', border: 'rgba(240,90,26,0.30)', sig: '#F05A1A' },
    green: { bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.30)', sig: '#22C55E' },
    red: { bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.30)', sig: '#EF4444' },
    blue: { bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.30)', sig: '#3B82F6' },
  };
  const t = tones[tone] || tones.orange;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex items-start gap-3"
      data-testid={testid}
    >
      <SabrilPixarAvatar size={size} />
      <div
        className="flex-1 min-w-0 rounded-2xl rounded-tl-md px-3.5 py-2.5 relative"
        style={{ background: t.bg, border: `1px solid ${t.border}` }}
      >
        <p className="text-[10px] font-black tracking-[2px] uppercase mb-1" style={{ color: t.sig }}>
          {kicker}
        </p>
        <p className="text-white text-[13px] leading-snug font-medium">
          {message}
        </p>
      </div>
    </motion.div>
  );
};

export default SabrilPixarAvatar;
